/**
 * 컴퍼넌트 레이아웃이 화면을 기준으로 위, 아래, 좌, 우에 붙어 있음을 지정
 * LookUpComponentDockMode 가 dock 인 경우만 해당
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
import { DomAttribute } from "../dom/DomAttribute.model";
import {FormContentConditionName} from "../form/FormContent.model";

export enum ComponentPosition {
    TOP = "dockTop",
    RIGHT = "dockRight",
    BOTTOM = "dockBottom",
    LEFT = "dockLeft",
    FLOAT = "float",
    WINDOW = "window"
}
export const LookUpComponentPosition = [
    { value: ComponentPosition.TOP, label: "top" },
    { value: ComponentPosition.RIGHT, label: "right" },
    { value: ComponentPosition.BOTTOM, label: "bottom" },
    { value: ComponentPosition.LEFT, label: "left" },
    { value: ComponentPosition.FLOAT, label: "float" },
    { value: ComponentPosition.WINDOW, label: "window" }
];

/**
 * 컴포넌트의 위치
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @enumerator
 */
export enum ComponentElementPosition {
    TOP = "top",
    RIGHT = "right",
    BOTTOM = "bottom",
    LEFT = "left"
}
export const LookUpComponentElementPosition = [
    { value: ComponentElementPosition.TOP, label: "top" },
    { value: ComponentElementPosition.RIGHT, label: "right" },
    { value: ComponentElementPosition.BOTTOM, label: "bottom" },
    { value: ComponentElementPosition.LEFT, label: "left" }
];

/**
 * 컴포넌트의 border 유무
 * ex) Tab 컴포넌트의 예
 * default: Tab 컴포넌트가 border를 가지는 경우 Top/Right/Bottom/Left 4방향 모두 border를 갖는다.
 * none: Tab 버튼이 위치하는 border를 제외한 나머지 3방향 border는 없음으로 세팅된다.
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @enumerator
 */
export enum ComponentElementBorder {
    NONE,
    DEFAULT
}
export const LookUpComponentElementBorder = [
    { value: ComponentElementBorder.NONE, label: "none" },
    { value: ComponentElementBorder.DEFAULT, label: "default" }
];

/**
 * 컨테이너의 overflow 속성
 * VISIBLE: 내용(Content)이 잘리지 않으며, 컨텐트 박스(Content box) 영역을 벗어나서 렌더링 될 수도 있다.
 * AUTO: x, y overflow=auto
 * AUTOX
 * AUTOY
 * HIDDEN
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @enumerator
 */
export enum ComponentElementOverflow {
    VISIBLE = "visible",
    AUTO = "auto",
    AUTOX = "auto-x",
    AUTOY = "auto-y",
    HIDDEN = "hidden"
}
export const LookUpComponentElementOverflow = [
    { value: ComponentElementOverflow.VISIBLE, label: "visible" },
    { value: ComponentElementOverflow.AUTO, label: "auto" },
    { value: ComponentElementOverflow.AUTOX, label: "auto-x" },
    { value: ComponentElementOverflow.AUTOY, label: "auto-y" },
    { value: ComponentElementOverflow.HIDDEN, label: "hidden" }
];

/**
 * 0, 1 값을 갖는 공통 LookUp
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @enumerator
 */
export enum BooleanByZeroOne {
    ZERO,
    ONE
}
export const LookUpBooleanByZeroOne = [
    { value: BooleanByZeroOne.ZERO, label: "zero" },
    { value: BooleanByZeroOne.ONE, label: "one" },
];

/**
 * YES, NO 값을 갖는 공통 LookUp
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @enumerator
 */
export enum BooleanByYesNo {
    YES = "yes",
    NO = "no"
}
export const LookUpBooleanByYesNo = [
    { value: BooleanByYesNo.YES, label: "yes" },
    { value: BooleanByYesNo.NO, label: "no" },
];

/**
 * Y, N 값을 갖는 공통 LookUp
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum BooleanByYN {
    Y = "Y",
    N = "N"
}
export const LookUpBooleanByYN = [
    { value: BooleanByYN.Y, label: "Y" },
    { value: BooleanByYN.N, label: "N" },
];

/**
 * true, false 값을 갖는 공통 LookUp
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @enumerator
 */
export enum BooleanByTrueFalse {
    FALSE,
    TRUE
}
export const LookUpBooleanByTrueFalse = [
    { value: BooleanByTrueFalse.TRUE, label: "True" },
    { value: BooleanByTrueFalse.FALSE, label: "False" }
];

/**
 * Calendar Hour Format LookUp
 *
 * @since 0.0.1
 * @author HwanSoon Park
 * @enumerator
 */
export enum CalendarHourFormat {
    HOUR12 = "12",
    HOUR24 = "24"
}
export const LookUpCalendarHourFormat = [
    { value: CalendarHourFormat.HOUR12, label: "12" },
    { value: CalendarHourFormat.HOUR24, label: "24" }
];

/**
 * Calendar View Type
 *
 * @since 0.0.1
 * @author HwanSoon Park
 * @enumerator
 */
export enum CalendarViewType {
    DATE = "date",
    MONTH = "month"
}
export const LookUpCalendarViewType = [
    { value: CalendarViewType.DATE, label: "date" },
    { value: CalendarViewType.MONTH, label: "month" }
];

/**
 * Calendar Selection Mode LookUp
 *
 * @since 0.0.1
 * @author HwanSoon Park
 * @enumerator
 */
export enum CalendarSelectionMode {
    SINGLE = "single",
    MULTIPLE = "multiple"
    // RANGE = "range"
}
export const LookUpCalendarSelectionMode = [
    { value: CalendarSelectionMode.SINGLE, label: "single" },
    { value: CalendarSelectionMode.MULTIPLE, label: "multiple" }
    // { value: CalendarSelectionMode.RANGE, label: "range" }
];

/**
 * Calendar Day
 * @since 0.0.1
 * @author Hwansoon Park
 * @enum
 */
export enum CalendarDay {
    SUNDAY = 0,
    MONDAY = 1,
    TUESDAY = 2,
    WEDNESDAY = 3,
    THURSDAY = 4,
    FRIDAY = 5,
    SATURDAY = 6
}

export const LookUpCalendarDay = [
    { value: CalendarDay.SUNDAY, label: "Sunday"},
    { value: CalendarDay.MONDAY, label: "Monday"},
    { value: CalendarDay.TUESDAY, label: "Tuesday"},
    { value: CalendarDay.WEDNESDAY, label: "Wednesday"},
    { value: CalendarDay.THURSDAY, label: "Thursday"},
    { value: CalendarDay.FRIDAY, label: "Friday"},
    { value: CalendarDay.SATURDAY, label: "Saturday"}
];

/**
 * AutoComplete SearchOption LookUp
 *
 * @since 0.0.1
 * @author HwanSoon Park
 * @enumerator
 */
export enum AutoCompleteSearchOption {
    STARTSWITH = "startsWith",
    INCLUDES = "includes",
    NONE = "none"
}
export const LookUpAutoCompleteSearchOption = [
    { value: AutoCompleteSearchOption.STARTSWITH, label: "startsWith" },
    { value: AutoCompleteSearchOption.INCLUDES, label: "includes" },
    { value: AutoCompleteSearchOption.NONE, label: "none" },
];

export enum AutoCompleteSearchField {
    LABEL = "label",
    LABELVALUE = "labelValue",
    VALUE = "value"
}
export const LookUpAutoCompleteSearchField = [
    { value: AutoCompleteSearchField.LABEL, label: "label" },
    { value: AutoCompleteSearchField.LABELVALUE, label: "label + value" },
    { value: AutoCompleteSearchField.VALUE, label: "value" }
];

/**
 * Preview Simulation
 *
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enumerator
 */
export enum PreviewSimulation {
    MOBILE = "mobile",
    TABLET = "tablet",
    DESKTOP = "desktop"
}
export const LookUpPreviewSimulation = [
    { value: PreviewSimulation.MOBILE, label: "Mobile" },
    { value: PreviewSimulation.TABLET, label: "Tablet" },
    { value: PreviewSimulation.DESKTOP, label: "Desktop" }
];

/**
 * Optional Window(Dialog) command state list
 * MINIMIZE: 최소화 상태
 * MAXIMIZE: 최대화 상태
 * DEFAULT: 원래 사이즈(최대화 취소)
 * DIALOG: 폼빌더내 다이알로그 모드
 * WINDOW: 외부 윈도우 모드
 *
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @enumerator
 */
export enum OptionalWindowCommandState {
    MINIMIZE = "minimize",
    MAXIMIZE = "maximize",
    DEFAULT = "default",
    DIALOG = "dialog",
    WINDOW = "window"
}

export const OptionalWindowCommandIconList = [
    { value: OptionalWindowCommandState.MINIMIZE, label: "Minimize" },
    { value: OptionalWindowCommandState.MAXIMIZE, label: "Maximize" },
    { value: OptionalWindowCommandState.WINDOW, label: "Window" }
];

export const OptionalWindowCommandStateList = [
    { value: OptionalWindowCommandState.MINIMIZE, label: "Minimize" },
    { value: OptionalWindowCommandState.MAXIMIZE, label: "Maximize" },
    { value: OptionalWindowCommandState.DEFAULT, label: "Default" },
    { value: OptionalWindowCommandState.DIALOG, label: "Dialog" },
    { value: OptionalWindowCommandState.WINDOW, label: "Window" }
];

/**
 * DataTable Paginator Position
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum DataTablePaginatorPosition {
    TOP = "top",
    BOTTOM = "bottom",
    BOTH = "both"
}
export const LookUpDataTablePaginatorPosition = [
    { value: DataTablePaginatorPosition.TOP, label: "Top" },
    { value: DataTablePaginatorPosition.BOTTOM, label: "Bottom" },
    { value: DataTablePaginatorPosition.BOTH, label: "Both" }
];

/**
 * DataTable Column Resize Mode
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum DataTableColumnResizeMode {
    FIT = "fit",
    EXPAND = "expand"
}
export const LookUpDataTableColumnResizeMode = [
    { value: DataTableColumnResizeMode.FIT, label: "Fit" },
    { value: DataTableColumnResizeMode.EXPAND, label: "Expand" }
];

/**
 * DataTable Selection Mode
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum DataTableSelectionMode {
    NONE = "none",
    SINGLE = "single",
    MULTIPLE = "multiple"
}
export const LookUpDataTableSelectionMode = [
    { value: null, label: "None" },
    { value: DataTableSelectionMode.SINGLE, label: "Single" },
    { value: DataTableSelectionMode.MULTIPLE, label: "Multiple" }
];

/**
 * DataTable Filter Match Mode
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum DataTableFilterMatchMode {
    STARTSWITH = "startsWith",
    CONTAINS = "contains",
    ENDSWITH = "endsWith",
    EQUALS = "equals",
    NOTEQUALS = "notEquals",
    DROPDOWN = "dropDown",
    MULTISELECT = "multiSelect",
    BETWEEN = "between"
}
export const LookUpDataTableFilterMatchMode = [
    { value: DataTableFilterMatchMode.STARTSWITH, label: "StartsWith" },
    { value: DataTableFilterMatchMode.CONTAINS, label: "Contains" },
    { value: DataTableFilterMatchMode.ENDSWITH, label: "EndsWith" },
    { value: DataTableFilterMatchMode.EQUALS, label: "Equals" },
    { value: DataTableFilterMatchMode.NOTEQUALS, label: "NotEquals" },
    { value: DataTableFilterMatchMode.DROPDOWN, label: "DropDown" },
    { value: DataTableFilterMatchMode.MULTISELECT, label: "MultiSelect" },
    { value: DataTableFilterMatchMode.BETWEEN, label: "Between" }
];

export enum ElementPosition {
    LEFT = "left",
    RIGHT = "right",
    TOP = "top",
    BOTTOM = "bottom"
}

/**
 * Button Icon Position
 *
 * @since 0.0.1
 * @author Byungmin Choi
 * @enumerator
 */
export const LookUpButtonIconPosition = [
    { value: ElementPosition.LEFT, label: "Left"},
    { value: ElementPosition.RIGHT, label: "Right"}
];

/**
 * Button Icon Position
 *
 * @since 0.0.1
 * @author Byungmin Choi
 * @enumerator
 */
export const LookUpElementPosition = [
    { value: ElementPosition.LEFT, label: "Left"},
    { value: ElementPosition.RIGHT, label: "Right"},
    { value: ElementPosition.TOP, label: "Top"},
    { value: ElementPosition.BOTTOM, label: "Bottom"}
];

/**
 * Editor mode
 *
 * @since 0.0.1
 * @author Byungmin Choi
 * @enumerator
 */
export enum EditorMode {
    SIMPLE = "simple",
    FULL = "full"
}

export const LookUpEditorMode = [
    { value: EditorMode.SIMPLE, label: "Simple"},
    { value: EditorMode.FULL, label: "Full"}
];

/**
 * SlideMenu ease attribute - transitionTimingFunction CSS
 *
 * @since 0.0.1
 * @author Byungmin Choi
 * @enumerator
 */
export enum SlideMenuEase {
    EASE = "ease",
    LINEAR = "linear",
    EASEIN = "ease-in",
    EASEOUT = "ease-out",
    EASEINOUT = "ease-in-out"
}

export const LookUpSlideMenuEase = [
    { value: SlideMenuEase.EASE, label: "ease"},
    { value: SlideMenuEase.LINEAR, label: "linear"},
    { value: SlideMenuEase.EASEIN, label: "ease-in"},
    { value: SlideMenuEase.EASEOUT, label: "ease-out"},
    { value: SlideMenuEase.EASEINOUT, label: "ease-in-out"}
];

/**
 * Tooltip Event
 * @since 0.0.1
 * @author Hwansoon Park
 * @enum
 */
export enum TooltipEvent {
    HOVER = "hover",
    FOCUS = "focus"
}
export const LookUpTooltipEvent = [
    { value: TooltipEvent.HOVER, label: "Hover" },
    { value: TooltipEvent.FOCUS, label: "Focus" }
];

/**
 * Modal Options for buttons
 *
 * @since 0.0.1
 * @author Heungsoo Ham
 * @const
 */
export const LookUpModalActionButtonOption = [
    { value: null, label: "None" },
    { value: DomAttribute.MODALBUTTONCLOSE, label: "Close" },
    { value: DomAttribute.MODALBUTTONAPPLY, label: "Apply" }
];

/**
 * Authority
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum AuthorityType {
    READONLY = "R",
    MODIFICATION = "M",
    ADMINISTRATOR = "A"
}
export const LookUpAuthorityType = [
    { value: AuthorityType.READONLY, label: "Readonly" },
    { value: AuthorityType.MODIFICATION, label: "Modification" },
    { value: AuthorityType.ADMINISTRATOR, label: "Administrator" }
];

/**
 * MemberType
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum MemberType {
    USER = "U",
    DEPARTMENT = "D",
    GROUP = "G"
}
export const LookUpMemberType = [
    { value: MemberType.USER, label: "User" },
    { value: MemberType.DEPARTMENT, label: "Department" },
    { value: MemberType.GROUP, label: "Group" }
];

/**
 * MenuActionType
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum MenuActionType {
    FORMSTATEONLY = "FORMSTATEONLY",
    LINKURL = "LINKURL"
}
export const LookUpMenuActionType = [
    { value: MenuActionType.FORMSTATEONLY, label: "FormState Only" },
    { value: MenuActionType.LINKURL, label: "Link URL" }
];

/**
 * File Unit
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum FileSizeUnit {
    "B",
    "KB",
    "MB",
    "GB",
    "TB"
}
export const LookUpFileSizeUnit = [
    { value: FileSizeUnit[0], label: FileSizeUnit[0] },
    { value: FileSizeUnit[1], label: FileSizeUnit[1] },
    { value: FileSizeUnit[2], label: FileSizeUnit[2] },
    { value: FileSizeUnit[3], label: FileSizeUnit[3] },
    { value: FileSizeUnit[4], label: FileSizeUnit[4] }
];

/**
 * Number locale String
 *
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enumerator
 */
export enum NumberLocaleString {
    LOCALE = "locale",
    COMMA = ",",
    DOT = ".",
    SPACE = " ",
    NO = ""
}

export const LookUpNumberLocaleString = [
    { value: NumberLocaleString.LOCALE, label: "Locale" },
    { value: NumberLocaleString.COMMA, label: "Comma" },
    { value: NumberLocaleString.DOT, label: "Dot" },
    { value: NumberLocaleString.SPACE, label: "Space" },
    { value: NumberLocaleString.NO, label: "No Separator" }
];

/**
 * Grid
 *
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enumerator
 */
export enum GridLayout {
    LIST = "list",
    GRID = "grid"
}

export const LookUpGridLayout = [
    { value: GridLayout.LIST, label: "List" },
    { value: GridLayout.GRID, label: "Grid" }
];

export enum GridNaviPos {
    BOTTOM = "bottom",
    TOP = "top",
    BOTH = "both"
}

export const LookUpGridNaviPos = [
    { value: GridNaviPos.BOTTOM, label: "Bottom" },
    { value: GridNaviPos.TOP, label: "Top" },
    { value: GridNaviPos.BOTH, label: "Both" }
];

export enum GridSortOrder {
    DESC = "desc",
    ASC = "asc"
}

export const LookUpGridSortOrder = [
    { value: GridSortOrder.DESC, label: "DESC" },
    { value: GridSortOrder.ASC, label: "ASC" },
];

export enum GridSortMode {
    SINGLE = "single",
    MULTIPLE = "multiple",
    NONE = "none"
}

export const LookUpGridSortMode = [
    { value: GridSortMode.SINGLE, label: "Single" },
    { value: GridSortMode.MULTIPLE, label: "Multiple" },
    { value: GridSortMode.NONE, label: "None" }
];

export enum GridColumnResizeMode {
    FIT = "fit",
    EXPAND = "expand"
}

export const LookUpGridColumnResizeMode = [
    { value: GridColumnResizeMode.FIT, label: "Fit" },
    { value: GridColumnResizeMode.EXPAND, label: "Expand" }
];

export enum GridTableCheckBOx {
    MULTIPLE = "multiple",
    SINGLE = "single",
    NONE = "none"
}

export const LookUpGridTableCheckBOx = [
    { value: GridTableCheckBOx.MULTIPLE, label: "Multiple" },
    { value: GridTableCheckBOx.SINGLE, label: "Single" },
    { value: GridTableCheckBOx.NONE, label: "None" }
];

 /**
 * FlexLayout FlexDirection
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum FlexDirection {
    ROW = "row",
    ROWREVERSE = "row-reverse",
    COLUMN = "column",
    COLUMNREVERSE = "column-reverse"
}

export const LookUpFlexDirection = [
    { label: FlexDirection.ROW, value: FlexDirection.ROW },
    { label: FlexDirection.ROWREVERSE, value: FlexDirection.ROWREVERSE },
    { label: FlexDirection.COLUMN, value: FlexDirection.COLUMN },
    { label: FlexDirection.COLUMNREVERSE, value: FlexDirection.COLUMNREVERSE }
];

/**
 * FlexLayout JustifyContent
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum JustifyContent {
    FLEXSTART = "flex-start",
    FLEXEND = "flex-end",
    CENTER = "center",
    SPACEBETWEEN = "space-between",
    SPACEAROUND = "space-around"
}

export const LookUpJustifyContent = [
    { label: JustifyContent.FLEXSTART, value: JustifyContent.FLEXSTART },
    { label: JustifyContent.FLEXEND, value: JustifyContent.FLEXEND },
    { label: JustifyContent.CENTER, value: JustifyContent.CENTER },
    { label: JustifyContent.SPACEBETWEEN, value: JustifyContent.SPACEBETWEEN },
    { label: JustifyContent.SPACEAROUND, value: JustifyContent.SPACEAROUND }
];

/**
 * FlexLayout AlignItems
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum AlignItems {
    FLEXSTART = "flex-start",
    FLEXEND = "flex-end",
    CENTER = "center",
    BASELINE = "baseline",
    STRETCH = "stretch"
}

export const LookUpAlignItems = [
    { label: AlignItems.FLEXSTART, value: AlignItems.FLEXSTART },
    { label: AlignItems.FLEXEND, value: AlignItems.FLEXEND },
    { label: AlignItems.CENTER, value: AlignItems.CENTER },
    { label: AlignItems.BASELINE, value: AlignItems.BASELINE },
    { label: AlignItems.STRETCH, value: AlignItems.STRETCH }
];

/**
 * FlexLayout AlignContent
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum AlignContent {
    FLEXSTART = "flex-start",
    FLEXEND = "flex-end",
    CENTER = "center",
    SPACEBETWEEN = "space-between",
    SPACEAROUND = "space-around",
    STRETCH = "stretch"
}

export const LookUpAlignContent = [
    { label: AlignContent.FLEXSTART, value: AlignContent.FLEXSTART },
    { label: AlignContent.FLEXEND, value: AlignContent.FLEXEND },
    { label: AlignContent.CENTER, value: AlignContent.CENTER },
    { label: AlignContent.SPACEBETWEEN, value: AlignContent.SPACEBETWEEN },
    { label: AlignContent.SPACEAROUND, value: AlignContent.SPACEAROUND },
    { label: AlignContent.STRETCH, value: AlignContent.STRETCH }
];

/**
 * FlexLayout AlignSelf
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum AlignSelf {
    FLEXSTART = "flex-start",
    FLEXEND = "flex-end",
    CENTER = "center",
    BASELINE = "baseline",
    STRETCH = "stretch"
}

export const LookUpAlignSelf = [
    { label: "Override align-items", value: null },
    { label: AlignSelf.FLEXSTART, value: AlignSelf.FLEXSTART },
    { label: AlignSelf.FLEXEND, value: AlignSelf.FLEXEND },
    { label: AlignSelf.CENTER, value: AlignSelf.CENTER },
    { label: AlignSelf.BASELINE, value: AlignSelf.BASELINE },
    { label: AlignSelf.STRETCH, value: AlignSelf.STRETCH }
];

/**
 * FlexLayout FlexWrap
 *
 * @since 0.0.1
 * @author Hwansoon Park
 * @enumerator
 */
export enum FlexWrap {
    NOWRAP = "nowrap",
    WRAP = "wrap",
    WRAPREVERSE = "wrap-reverse"
}

export const LookUpFlexWrap = [
    { label: FlexWrap.NOWRAP, value: FlexWrap.NOWRAP },
    { label: FlexWrap.WRAP, value: FlexWrap.WRAP },
    { label: FlexWrap.WRAPREVERSE, value: FlexWrap.WRAPREVERSE }
];

/**
 * PreventActionType
 *
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enumerator
 */
export const LookUpPreventActionType = [
    { value: FormContentConditionName.READONLY, label: "Read Only" },
    { value: FormContentConditionName.PRINTABLE, label: "Printable" },
    { value: FormContentConditionName.DISABLED, label: "Disabled" },
    { value: FormContentConditionName.VISIBILITYHIDDEN, label: "Visibility Hidden" },
    { value: FormContentConditionName.EDITABLE, label: "Editable" }
];
